import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import CollapsibleCard from '@spark/ui-components/components/CollapsibleCard';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import {
  getInfracaoPremiacaoById,
  createInfracaoPremiacao,
  updateInfracaoPremiacao,
  deleteInfracaoPremiacao
} from '@spark/services/premiacao';
import { infracaoFilterConfig } from '../config/infracaoFilterConfig';
import { getInfracaoColumns } from '../config/infracaoColumns';
import {
  emptyInfracaoForm,
  infracaoFormSections,
  infracaoFormStructure,
  validateInfracaoForm
} from '../config/infracaoFormConfig';
import { usePremiacaoInfracaoSelection } from '../hooks/usePremiacaoInfracaoSelection';
import PremiacaoInfracaoMotoristaModal from './PremiacaoInfracaoMotoristaModal';
import PremiacaoImportacaoExcelModal from './PremiacaoImportacaoExcelModal';
import { importarInfracaoPremiacoes, exportarLayoutImportacaoInfracaoPremiacoesComRegistros } from '@spark/services/premiacao';
import { LAYOUT_IMPORTACAO_INFRACOES } from '../config/premiacaoImportLayouts';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toRowModel = (item) => ({
  id: pick(item, 'id', 'Id'),
  descricao: pick(item, 'descricao', 'Descricao') || '',
  descricaoGrupo: pick(item, 'descricaoGrupo', 'DescricaoGrupo') || '',
  descricaoPlanoAcao: pick(item, 'descricaoPlanoAcao', 'DescricaoPlanoAcao') || '',
  porcentagemDesconto: pick(item, 'porcentagemDesconto', 'PorcentagemDesconto')
});

const mapApiToFormValues = (data) => ({
  id: pick(data, 'id', 'Id') ?? '',
  descricao: pick(data, 'descricao', 'Descricao') || '',
  descricaoGrupo: pick(data, 'descricaoGrupo', 'DescricaoGrupo') || '',
  descricaoPlanoAcao: pick(data, 'descricaoPlanoAcao', 'DescricaoPlanoAcao') || '',
  porcentagemDesconto: pick(data, 'porcentagemDesconto', 'PorcentagemDesconto') ?? ''
});

const buildPayload = (values) => ({
  Descricao: normalizeText(values.descricao),
  DescricaoGrupo: normalizeText(values.descricaoGrupo),
  DescricaoPlanoAcao: normalizeText(values.descricaoPlanoAcao),
  PorcentagemDesconto: parseNumber(values.porcentagemDesconto)
});

const PremiacaoInfracaoModal = ({ show, onHide }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyInfracaoForm);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [showMotoristasModal, setShowMotoristasModal] = useState(false);
  const [showImportacaoModal, setShowImportacaoModal] = useState(false);
  const {
    infracoes,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = usePremiacaoInfracaoSelection(show);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyInfracaoForm);
      setLoadingEditId(null);
      setLoadingDeleteId(null);
      setIsFormCollapsed(true);
      setShowMotoristasModal(false);
      setShowImportacaoModal(false);
    }
  }, [show]);

  useEffect(() => {
    if (!editingId) {
      setShowMotoristasModal(false);
    }
  }, [editingId]);

  const handleEdit = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const data = await getInfracaoPremiacaoById(id);
      setEditingId(id);
      setFormInitialValues(mapApiToFormValues(data));
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao carregar infracao');
      console.error('Erro ao carregar infracao:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [loadingEditId]);

  const handleDelete = useCallback(async (id) => {
    if (!id || loadingDeleteId === id) return;

    try {
      setLoadingDeleteId(id);
      await deleteInfracaoPremiacao(id);
      notification.success('Infracao excluida com sucesso!');
      if (editingId === id) {
        setEditingId(null);
        setFormInitialValues(emptyInfracaoForm);
        setShowMotoristasModal(false);
      }
      reload();
    } catch (error) {
      notification.error('Erro ao excluir infracao');
      console.error('Erro ao excluir infracao:', error);
    } finally {
      setLoadingDeleteId(null);
    }
  }, [editingId, loadingDeleteId, reload]);

  const handleClear = useCallback((form) => {
    setEditingId(null);
    setFormInitialValues(emptyInfracaoForm);
    form.reset(emptyInfracaoForm);
    setIsFormCollapsed(false);
    setShowMotoristasModal(false);
  }, []);

  const handleSubmit = useCallback(async (values) => {
    const payload = buildPayload(values);

    try {
      setSaving(true);
      let data;

      if (values.id) {
        data = await updateInfracaoPremiacao(values.id, payload);
        notification.success('Infracao atualizada com sucesso!');
      } else {
        data = await createInfracaoPremiacao(payload);
        notification.success('Infracao criada com sucesso!');
      }

      setEditingId(pick(data, 'id', 'Id') || values.id || null);
      setFormInitialValues(mapApiToFormValues(data));
      reload();
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao salvar infracao');
      console.error('Erro ao salvar infracao:', error);
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const rows = Array.isArray(infracoes) ? infracoes.map(toRowModel) : [];
  const formKey = useMemo(() => (editingId ? `edit-${editingId}` : 'create'), [editingId]);
  const motoristasDisabled = !editingId;
  const motoristasButtonStyle = motoristasDisabled
    ? { backgroundColor: 'transparent', borderColor: '#c9ced6', color: '#8a93a1' }
    : undefined;
  const infracaoContexto = editingId
    ? `${formInitialValues.descricao || 'Infracao'} #${editingId}`
    : null;

  return (
    <>
      <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
        <Modal.Header closeButton>
          <Modal.Title>Infrações de Premiação</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <FilterPanel
            filters={infracaoFilterConfig}
            onApply={handleFiltrosChange}
            title="Filtros de Busca"
            loading={loading}
            defaultCollapsed={false}
          />

          <TanStackTable
            key={`premiacao-infracao-${paginaAtual}-${quantidadePorPagina}`}
            columns={getInfracaoColumns(handleEdit, handleDelete, loadingEditId, loadingDeleteId)}
            data={rows}
            loading={loading}
            withPagination={true}
            pageSizeOptions={[5, 10, 25, 50]}
            enableRowColors={true}
            className="mt-3"
            manualPagination={true}
            totalItems={totalRegistros}
            controlledPagination={{
              pageIndex: Math.max(0, (paginaAtual || 1) - 1),
              pageSize: quantidadePorPagina || 10
            }}
            onPaginationChange={handlePaginationChange}
            enableExportExcel={true}
            exportFileName="premiacao-infracoes"
          />

          <CollapsibleCard
            className="mt-3"
            title="Formulário de Infração"
            isCollapsed={isFormCollapsed}
            onToggle={setIsFormCollapsed}
          >
            <Form
              key={formKey}
              onSubmit={handleSubmit}
              initialValues={formInitialValues}
              validate={validateInfracaoForm}
            >
              {({ handleSubmit: onFormSubmit, form }) => (
                <FormContainer onSubmit={onFormSubmit}>
                  <DynamicForm
                    structure={infracaoFormStructure}
                    sections={infracaoFormSections()}
                    readOnly={false}
                  />

                  <FormButtonToolbar className="mt-3">
                    <Button variant="secondary" type="button" onClick={() => handleClear(form)} disabled={saving}>
                      Limpar
                    </Button>
                    <Button
                      variant="outline-primary"
                      type="button"
                      onClick={() => setShowMotoristasModal(true)}
                      disabled={motoristasDisabled}
                      style={motoristasButtonStyle}
                    >
                      Motoristas
                    </Button>
                    <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                      Salvar
                    </ButtonWithLoading>
                  </FormButtonToolbar>
                </FormContainer>
              )}
            </Form>
          </CollapsibleCard>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted">
              {totalRegistros > 0 && (
                <span>
                  Exibindo {rows.length} de {totalRegistros} infracao(oes)
                </span>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={() => setShowImportacaoModal(true)}>
                Importar Infracoes
              </Button>
              <Button variant="secondary" onClick={onHide}>
                Fechar
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <PremiacaoInfracaoMotoristaModal
        show={showMotoristasModal}
        onHide={() => setShowMotoristasModal(false)}
        idInfracao={editingId}
        infracaoContexto={infracaoContexto}
      />

      <PremiacaoImportacaoExcelModal
        show={showImportacaoModal}
        onClose={() => setShowImportacaoModal(false)}
        onImportado={reload}
        title="Importar Infrações de Premiação"
        layoutColumns={LAYOUT_IMPORTACAO_INFRACOES.columns}
        layoutObservacoes={LAYOUT_IMPORTACAO_INFRACOES.observacoes}
        layoutFileName={LAYOUT_IMPORTACAO_INFRACOES.fileName}
        layoutFileNameComRegistros={LAYOUT_IMPORTACAO_INFRACOES.fileNameComRegistros}
        onImport={importarInfracaoPremiacoes}
        onDownloadLayoutComRegistros={exportarLayoutImportacaoInfracaoPremiacoesComRegistros}
      />
    </>
  );
};

export default PremiacaoInfracaoModal;
